import { useState, useCallback, useEffect } from 'react';
import {
  RowTab,
  ParquetFormState,
  Timeframe,
  DataSource,
  DataType,
} from './types';
import { apiFetch } from '../../auth/AuthContext';

/**
 * useParquetStore — backed by user_management.parquet_requests in ClickHouse.
 * All operations go through auth_server.py (port 8002) with JWT auth.
 *
 * Admin users see all parquet configs across all users.
 * Regular users see only their own.
 */

interface ApiRow {
  request_id: string;
  owner_id: string;
  name: string;
  coins: string[];
  timeframes: string[];
  data_types: string[];
  data_source: string;
  date_from: string;
  date_to: string;
  created_at: string;
  updated_at: string;
}

function rowToTab(r: ApiRow): RowTab {
  return {
    id: r.request_id,
    name: r.name,
    timeframes: r.timeframes as Timeframe[],
    dataSource: r.data_source as DataSource,
    dataTypes: r.data_types as DataType[],
    coins: r.coins,
    dateFrom: r.date_from,
    dateTo: r.date_to,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function formToBody(form: ParquetFormState) {
  return {
    name: form.name,
    coins: form.coins,
    timeframes: form.timeframes,
    data_types: form.dataTypes,
    data_source: form.dataSource,
    date_from: form.dateFrom,
    date_to: form.dateTo,
  };
}

export function useParquetStore() {
  const [rowTabs, setRowTabs] = useState<RowTab[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [timeframeFilter, setTimeframeFilter] = useState<Timeframe | 'all'>(
    'all',
  );
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const refresh = useCallback(async () => {
    try {
      const r = await apiFetch('/parquet/list');
      if (!r.ok) return;
      const data = await r.json();
      setRowTabs((data.requests as ApiRow[]).map(rowToTab));
    } catch {
      /* keep current state on network error */
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addRowTab = useCallback(
    async (form: ParquetFormState) => {
      const r = await apiFetch('/parquet/create', {
        method: 'POST',
        body: JSON.stringify(formToBody(form)),
      });
      if (r.ok) await refresh();
    },
    [refresh],
  );

  const updateRowTab = useCallback(
    async (id: string, form: ParquetFormState) => {
      const r = await apiFetch('/parquet/update', {
        method: 'POST',
        body: JSON.stringify({ request_id: id, ...formToBody(form) }),
      });
      if (r.ok) await refresh();
    },
    [refresh],
  );

  const deleteRowTab = useCallback(
    async (id: string) => {
      const r = await apiFetch(
        `/parquet/delete?request_id=${encodeURIComponent(id)}`,
        {
          method: 'POST',
        },
      );
      if (r.ok) await refresh();
    },
    [refresh],
  );

  const filteredTabs = rowTabs.filter((tab) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const nameMatch = tab.name.toLowerCase().includes(q);
      const coinMatch = tab.coins.some((c) => c.toLowerCase().includes(q));
      if (!nameMatch && !coinMatch) return false;
    }
    if (
      timeframeFilter !== 'all' &&
      !tab.timeframes.includes(timeframeFilter)
    ) {
      return false;
    }
    if (filterDateFrom && tab.dateTo < filterDateFrom) return false;
    if (filterDateTo && tab.dateFrom > filterDateTo) return false;
    return true;
  });

  return {
    rowTabs: filteredTabs,
    allRowTabs: rowTabs,
    searchQuery,
    setSearchQuery,
    timeframeFilter,
    setTimeframeFilter,
    filterDateFrom,
    setFilterDateFrom,
    filterDateTo,
    setFilterDateTo,
    addRowTab,
    updateRowTab,
    deleteRowTab,
    refresh,
  };
}
